import type { FeatureFlagAdapter } from '../adapters/types';
import type { EvaluationContext, FlagPropagator, FlagSnapshot, FlagStore } from './types';

export interface FlagStoreOptions {
  initialContext?: EvaluationContext;
  propagators?: FlagPropagator[];
}

export function createFlagStore(adapter: FeatureFlagAdapter, options?: FlagStoreOptions): FlagStore {
  const { initialContext, propagators } = options ?? {};
  let context: EvaluationContext = initialContext ?? {};
  let flags: FlagSnapshot = adapter.getFlags(context);
  const listeners = new Set<() => void>();

  function propagate() {
    if (propagators) {
      for (const propagator of propagators) {
        try {
          propagator(flags);
        } catch {
          // Isolate propagator failures — one broken propagator must not block others or emitChange()
        }
      }
    }
  }

  function emitChange() {
    for (const listener of listeners) {
      listener();
    }
  }

  // Only the store holds the context, so it re-reads rather than trusting a pushed snapshot.
  const unsubscribeAdapter = adapter.subscribe?.(() => {
    flags = adapter.getFlags(context);
    propagate();
    emitChange();
  });

  // Propagate initial flag state
  propagate();

  return {
    getFlag(key: string) {
      return flags[key];
    },

    getSnapshot() {
      return flags;
    },

    getContext() {
      return context;
    },

    setContext(ctx: EvaluationContext) {
      context = { ...context, ...ctx };
      flags = adapter.getFlags(context);
      propagate();
      emitChange();
    },

    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    destroy() {
      listeners.clear();
      unsubscribeAdapter?.();
      adapter.destroy?.();
    },
  };
}
